import React, { Fragment, useMemo, useState, useEffect } from 'react';
import { MenuItem, Menu } from '@mui/material';
import {
    TextTiny,
    ProgressWithNumber,
    NestedMenuItem,
    DarkArrowTooltipIcon,
} from '@awarego-ui/components';
import RequireRole from '../../RequireRole';
import NameAndSublistCell from '../../../components/manager/NameAndSublistCell';
import DateCell from '../../../components/manager/DateCell';
import TableClient from '../../../components/table/TableClient';
import { LINKS } from '../../../utils/constants';

function ScormAutomationListTable({
    automations,
    onDelete,
    onDownloadPackage,
    isPackageDownloading,
    defaultSortBy,
    readOnly = false,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [automation, setOpenedAutomation] = useState(null);
    const rowsPerPage = 10;

    const handleDownloadPackage = (type) => {
        if (onDownloadPackage && automation) {
            onDownloadPackage(automation.id, automation._rawData.uniqueId, type);
        }
    };

    useEffect(() => {
        if (isPackageDownloading === false) {
            handleMenuClose();
        }
    }, [isPackageDownloading]);

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: (x) => (
                    <NameAndSublistCell
                        name={x.value}
                        maxVisibleNameLength={30}
                        subListItems={x.row.original.subjectsNames}
                        maxVisibleSubListCount={1}
                        linkName={`/trainings/p${x.row.original.id}/view`}
                    />
                ),
            },
            {
                Header: '# Employees',
                accessor: '_rawData.totalUsers',
                Cell: (x) => <TextTiny lighter>{x.value || 0}</TextTiny>,
                sortDescFirst: true,
            },
            {
                Header: 'Created',
                accessor: '_rawData.created',
                Cell: (x) => <DateCell date={x.value} />,
                minWidth: 112,
                sortDescFirst: true,
            },
            {
                Header: 'Avg. progress',
                accessor: 'awarenessScore',
                Cell: (x) =>
                    x.value ? (
                        <ProgressWithNumber value={x.value} height={6} />
                    ) : (
                        ''
                    ),
                sortDescFirst: true,
            },
            ...(readOnly
                ? []
                : [
                      {
                          Header: ' ',
                          Cell: (x) => (
                              <DarkArrowTooltipIcon
                                  customFunction={handleMenuOpen}
                                  row={x.row.original}
                                  title="More"
                                  icon="more"
                              />
                          ),
                      },
                  ]),
            {
                Header: 'Last Updated',
                accessor: '_rawData.updated',
                disableSortBy: false,
                disableFilters: false,
                show: false,
            },
        ],
        [readOnly]
    );

    const handleMenuOpen = (e, aut) => {
        setOpenedAutomation(aut);
        setAnchorEl(e.currentTarget);
        e.stopPropagation();
    };

    const handleMenuClose = (e) => {
        setOpenedAutomation(null);
        setAnchorEl(null);
        if (e) e.stopPropagation();
    };

    return (
        <Fragment>
            <TableClient
                data={automations}
                columns={columns}
                link={() => LINKS.training}
                defaultSortBy={defaultSortBy}
                hiddenColumns={['_rawData.updated']}
                initialPageSize={rowsPerPage}
                hidePagination={automations.length <= rowsPerPage}
            />
            {automation && (
                <Menu
                    id="scorm-list-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={automation != null}
                    onClose={handleMenuClose}
                    elevation={1}
                >
                    <NestedMenuItem
                        text="Download training"
                        loading={isPackageDownloading}
                        items={[
                            {
                                text: 'SCORM 1.2',
                                props: {
                                    onClick: () =>
                                        handleDownloadPackage('scorm12'),
                                },
                            },
                            {
                                text: 'SCORM 2004 3rd Edition',
                                props: {
                                    onClick: () =>
                                        handleDownloadPackage('scorm2004'),
                                },
                            },
                            {
                                text: 'cmi5',
                                props: {
                                    onClick: () =>
                                        handleDownloadPackage('cmi5'),
                                },
                            },
                        ]}
                    />
                    <RequireRole>
                        <MenuItem
                            onClick={(e) => {
                                onDelete(automation.id, true);
                                handleMenuClose(e);
                            }}
                        >
                            <span style={{ color: 'red' }}>Delete</span>
                        </MenuItem>
                    </RequireRole>
                </Menu>
            )}
        </Fragment>
    );
}

export default ScormAutomationListTable;
